import React, { useState, useEffect } from 'react';
import {
  MessageSquare,
  Plus,
  Search,
  Pin,
  Lock,
  Eye,
  MessageCircle,
  Trash2,
  Edit2,
  X,
  Filter,
} from 'lucide-react';
import { forumService } from '@/services/forum.service';
import type {
  ForumThreadDto,
  GetForumThreadsDto,
  ThreadSortBy,
} from '@/types/forum.types';
import {
  getThreadStatusBadge,
  formatThreadDate,
  getThreadAuthorName,
  getThreadAuthorInitials,
} from '@/types/forum.types';
import ThreadForm from './ThreadForm';
import ThreadDetailView from '../forum/ThreadDetailView';
import DeleteConfirmationModal from '@/components/ui/DeleteConfirmationModal';
import { SkeletonLoader } from '@/components/ui/SkeletonLoader';
import toast from 'react-hot-toast';
import { useAuth } from '@/contexts/AuthContext';

interface ForumThreadsProps {
  courseId: string;
  courseName?: string;
}

export default function ForumThreads({ courseId, courseName }: ForumThreadsProps) {
  const [threads, setThreads] = useState<ForumThreadDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalThreads, setTotalThreads] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [sortBy, setSortBy] = useState<ThreadSortBy>('latest');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingThread, setEditingThread] = useState<ForumThreadDto | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [pinnedOnly, setPinnedOnly] = useState(false);
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [threadToDelete, setThreadToDelete] = useState<ForumThreadDto | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const limit = 10;
const { user } = useAuth();
  // Load threads
  const loadThreads = async () => {
    try {
      setLoading(true);

      // Validate courseId
      if (!courseId) {
        console.error('CourseId is missing or invalid:', courseId);
        toast.error('Course ID is missing');
        return;
      }

      const params: GetForumThreadsDto = {
        course_id: courseId,
        page,
        limit,
        sort_by: sortBy,
        is_active: true,
      };

      if (searchText) {
        params.search_text = searchText;
      }

      if (categoryFilter) {
        params.category = categoryFilter;
      }

      if (pinnedOnly) {
        params.is_pinned = true;
      }

      const response = await forumService.getThreads(params);

      // Handle response structure
      if (!response) {
        toast.error('Invalid response from server');
        return;
      }

      // Set threads (default to empty array if undefined)
      setThreads(response.threads || []);

      setTotalThreads(response.total || 0);

      // Use total_pages from backend, or calculate it
      if (response.total_pages) {
        setTotalPages(response.total_pages);
      } else {
        setTotalPages(Math.ceil((response.total || 0) / limit));
      }
    } catch (error: any) {
      console.error('Error loading threads:', error);
      console.error('Error details:', {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status,
      });
      toast.error(error?.response?.data?.description || error?.message || 'Failed to load forum threads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadThreads();
  }, [courseId, page, sortBy, searchText, categoryFilter, pinnedOnly]);

  // Handle thread creation
  const handleThreadCreated = (newThread: ForumThreadDto) => {
    setShowCreateModal(false);
    setThreads([newThread, ...threads]);
    setTotalThreads(totalThreads + 1);
    toast.success('Thread created successfully!');
  };

  // Handle thread update
  const handleThreadUpdated = (updatedThread: ForumThreadDto) => {
    setEditingThread(null);
    setThreads(threads.map(t => t._id === updatedThread._id ? updatedThread : t));
    toast.success('Thread updated successfully!');
  };

  // Handle thread deletion - open modal
  const handleDeleteThread = (thread: ForumThreadDto) => {
    setThreadToDelete(thread);
    setDeleteModalOpen(true);
  };

  // Confirm thread deletion
  const confirmDeleteThread = async () => {
    if (!threadToDelete) return;

    try {
      setDeleteLoading(true);
      await forumService.deleteThread(threadToDelete._id);
      setThreads(threads.filter(t => t._id !== threadToDelete._id));
      setTotalThreads(totalThreads - 1);
      toast.success('Thread deleted successfully!');
      setDeleteModalOpen(false);
      setThreadToDelete(null);
    } catch (error: any) {
     
      const errorMessage = error?.response?.data?.error?.error_description
        || error?.response?.data?.description
        || error?.message
        || 'Failed to delete thread';
      toast.error(errorMessage);
    } finally {
      setDeleteLoading(false);
    }
  };

  // Handle toggle pin
  const handleTogglePin = async (thread: ForumThreadDto) => {
    try {
      const updated = await forumService.togglePinThread(thread._id, !thread.is_pinned);
      // Preserve creator field from original thread
      setThreads(threads.map(t =>
        t._id === updated._id ? { ...updated, creator: t.creator } : t
      ));
      toast.success(updated.is_pinned ? 'Thread pinned' : 'Thread unpinned');
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error?.error_description
        || error?.response?.data?.description
        || error?.message
        || 'Failed to update thread';
      toast.error(errorMessage);
    }
  };

  // Handle toggle lock
  const handleToggleLock = async (thread: ForumThreadDto) => {
    try {
      const updated = await forumService.toggleLockThread(thread._id, !thread.is_locked);
      // Preserve creator field from original thread
      setThreads(threads.map(t =>
        t._id === updated._id ? { ...updated, creator: t.creator } : t
      ));
      toast.success(updated.is_locked ? 'Thread locked' : 'Thread unlocked');
    } catch (error: any) {
     
      const errorMessage = error?.response?.data?.error?.error_description
        || error?.response?.data?.description
        || error?.message
        || 'Failed to update thread';
      toast.error(errorMessage);
    }
  };

  // Search handler with debounce
  const handleSearch = (value: string) => {
    setSearchText(value);
    setPage(1);
  };

  // Handle thread click to open detail view
  const handleThreadClick = (threadId: string) => {
    setSelectedThreadId(threadId);
  };

  // Handle back from detail view
  const handleBackToList = () => {
    setSelectedThreadId(null);
    // Reload threads to get updated counts
    loadThreads();
  };

  // Handle thread update from detail view
  const handleThreadUpdateFromDetail = (updated: ForumThreadDto) => {
    setThreads(threads.map((t) => (t._id === updated._id ? updated : t)));
  };

  // Handle thread delete from detail view
  const handleThreadDeleteFromDetail = (threadId: string) => {
    setThreads(threads.filter((t) => t._id !== threadId));
    setTotalThreads(totalThreads - 1);
    setSelectedThreadId(null);
  };

  // Show detail view if thread is selected
  if (selectedThreadId) {
    return (
      <ThreadDetailView
        threadId={selectedThreadId}
        courseId={courseId}
        onBack={handleBackToList}
        onThreadUpdate={handleThreadUpdateFromDetail}
        onThreadDelete={handleThreadDeleteFromDetail}
      />
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-brand-indigo-600" />
            <h2 className="text-lg font-semibold text-gray-900">Forum</h2>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-brand-indigo-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Thread
          </button>
        </div>

        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search threads..."
            value={searchText}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-full pl-9 pr-10 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-indigo-500 focus:border-transparent"
          />
          {searchText && (
            <button
              onClick={() => handleSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Filters and Sort */}
        <div className="flex items-center gap-2 mt-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <Filter className="w-4 h-4" />
            Filters
          </button>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as ThreadSortBy)}
            className="min-w-0 flex-1 sm:flex-none px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-indigo-500 focus:border-transparent"
          >
            <option value="latest">Latest</option>
            <option value="popular">Popular</option>
            <option value="most_discussed">Most Discussed</option>
          </select>

          <div className="hidden sm:block flex-1" />

          <span className="text-xs text-gray-500 whitespace-nowrap flex-shrink-0">
            {totalThreads} thread{totalThreads !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Filter Options */}
        {showFilters && (
          <div className="flex gap-2 mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={pinnedOnly}
                onChange={(e) => setPinnedOnly(e.target.checked)}
                className="rounded border-gray-300 text-brand-indigo-600 focus:ring-brand-indigo-500"
              />
              <span className="text-gray-700">Pinned only</span>
            </label>
            <input
              type="text"
              placeholder="Category filter..."
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-indigo-500"
            />
          </div>
        )}
      </div>

      {/* Thread List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="divide-y divide-gray-200">
            {Array.from({ length: 5 }).map((_, index) => (
              <div key={index} className="p-4 bg-white">
                <div className="flex gap-3">
                  {/* Avatar Skeleton */}
                  <SkeletonLoader type="avatar" />

                  {/* Content Skeleton */}
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2">
                      <SkeletonLoader type="title" className="h-5 w-1/3" />
                    </div>
                    <SkeletonLoader type="text" count={2} className="h-3" />
                    <div className="flex items-center gap-4 mt-2">
                      <SkeletonLoader type="custom" className="h-3 w-12" />
                      <SkeletonLoader type="custom" className="h-3 w-12" />
                      <SkeletonLoader type="custom" className="h-3 w-24" />
                      <SkeletonLoader type="custom" className="h-3 w-20" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : threads.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-500">
            <MessageSquare className="w-12 h-12 mb-3 text-gray-300" />
            <p className="text-sm font-medium">No threads yet</p>
            <p className="text-xs text-gray-400 mt-1">Start a discussion!</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {threads.map((thread) => {
              const isAuthor = user?.id === thread.created_by || user?.id === thread.creator?._id;
              const statusBadge = getThreadStatusBadge(thread);
              const authorName = getThreadAuthorName(thread);
              const authorInitials = getThreadAuthorInitials(thread);

              return (
                <div
                  key={thread._id}
                  className="p-4 bg-white hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => handleThreadClick(thread._id)}
                >
                  <div className="flex gap-3">
                    {/* Author Avatar */}
                    <div className="flex-shrink-0">
                      {thread.creator?.profile_picture ? (
                        <img
                          src={thread.creator.profile_picture}
                          alt={authorName}
                          className="w-10 h-10 rounded-full"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-brand-indigo-100 flex items-center justify-center text-brand-indigo-600 font-medium text-sm">
                          {authorInitials}
                        </div>
                      )}
                    </div>

                    {/* Thread Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 break-words min-w-0" title={thread.title}>
                              {thread.title}
                            </h3>
                            {thread.is_pinned && (
                              <Pin className="w-3.5 h-3.5 text-brand-blue-600 flex-shrink-0" />
                            )}
                            {thread.is_locked && (
                              <Lock className="w-3.5 h-3.5 text-red-600 flex-shrink-0" />
                            )}
                            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusBadge.className}`}>
                              {statusBadge.label}
                            </span>
                          </div>

                          <p className="text-xs text-gray-600 mt-1 line-clamp-2"title= {thread.description}>
                            {thread.description}
                          </p>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-0.5 lg:gap-1 flex-shrink-0 -mr-2 -mt-1 lg:mr-0 lg:mt-0">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTogglePin(thread);
                            }}
                            className="p-2 lg:p-1.5 text-gray-400 hover:text-brand-blue-600 hover:bg-brand-blue-50 rounded transition-colors"
                            title={thread.is_pinned ? 'Unpin' : 'Pin'}
                          >
                          <Pin className="w-4 h-4" />
                          </button>
                         {/* <button
                     onClick={(e) => {
                     e.stopPropagation();
                   if (!isAuthor) return;
                   handleToggleLock(thread);
                    }}
                 disabled={!isAuthor}
                className={`p-1.5 rounded transition-colors ${
               isAuthor? thread.is_locked
              ? 'text-red-600 hover:bg-red-50'
            : 'text-gray-400 hover:text-red-600 hover:bg-red-50'
            : 'text-gray-300 cursor-not-allowed opacity-60'
            }`}
           title={
           isAuthor? thread.is_locked
             ? 'Unlock Thread': 'Lock Thread': 'Only the creator can lock this thread'
            }
            >
              <Lock className="w-4 h-4" />
                </button> */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!isAuthor) return;
                              setEditingThread(thread);
                            }}
                            disabled={!isAuthor}
                           className={`p-2 lg:p-1.5 rounded transition-colors ${
                            isAuthor? 'text-gray-400 hover:text-red-600 hover:bg-red-50': 'text-gray-300 cursor-not-allowed opacity-60' }`}
                            
                           title={isAuthor ? 'Edit Thread' : 'Only the creator can edit this thread'}
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                               if (!isAuthor) return;
                              handleDeleteThread(thread);
                            }}
                             disabled={!isAuthor}
                            className={`p-2 lg:p-1.5 rounded transition-colors ${
                            isAuthor? 'text-gray-400 hover:text-red-600 hover:bg-red-50': 'text-gray-300 cursor-not-allowed opacity-60' }`}
                           title={isAuthor ? 'Delete Thread' : 'Only the creator can delete this thread'}
                           >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      {/* Full width under the title row, so the action icons don't squeeze it on phones */}
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-gray-500 min-w-0">
                        {/* <span className="flex items-center gap-1">
                          <Eye className="w-3.5 h-3.5" />
                          {thread.view_count}
                        </span> */}
                        <span className="flex items-center gap-1 whitespace-nowrap">
                          <MessageCircle className="w-3.5 h-3.5" />
                          {thread.discussion_count}
                        </span>
                        <span className="min-w-0 truncate">{authorName}</span>
                        <span>•</span>
                        <span className="whitespace-nowrap">{formatThreadDate(thread.createdAt)}</span>
                      </div>

                      {thread.tags && thread.tags.length > 0 && (
                        <div className="flex gap-1 mt-2 flex-wrap">
                          {thread.tags.map((tag, idx) => (
                            <span
                              key={idx}
                              className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded"
                            >
                              #{tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="border-t border-gray-200 bg-white px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-sm text-gray-600">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Create Thread Modal */}
      {showCreateModal && (
        <ThreadForm
          courseId={courseId}
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleThreadCreated}
        />
      )}

      {/* Edit Thread Modal */}
      {editingThread && (
        <ThreadForm
          courseId={courseId}
          thread={editingThread}
          onClose={() => setEditingThread(null)}
          onSuccess={handleThreadUpdated}
        />
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setThreadToDelete(null);
        }}
        onConfirm={confirmDeleteThread}
        title="Delete Thread"
        description="Are you sure you want to delete this thread? All discussions and comments will also be permanently deleted."
        itemName={threadToDelete?.title}
        isLoading={deleteLoading}
      />
    </div>
  );
}
